import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronRight,
  Search,
  AlertTriangle,
  Package,
  Layers,
  Upload,
  CheckCircle2,
  Phone,
  Hash,
} from "lucide-react";
import { useAuth } from "../../utils/auth";
import { canCreateTickets } from "../../utils/roles";
import { searchOrdersByPhone, fetchOrderById } from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import {
  createTicket,
  fetchActiveTicketsForOrder,
  fetchTicketOrderContext,
  fetchTicketProblemTypes,
  fetchAssignableUsers,
  fileToTicketAttachment,
  TICKET_PRIORITIES,
} from "../../api/TicketAPIs";
import { AttachmentPreview, TicketStatusBadge, TicketPriorityBadge } from "./TicketBadges";
import {
  normalizePhoneNumber,
  PRIORITY_LABELS_AR,
  formatDateTime,
  resolveMediaUrl,
} from "./ticketUi";

const emptyIssueForm = () => ({
  problemTypeId: "",
  description: "",
  attachments: [],
});

const pick = (obj, ...keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
};

const isLineItemKind = (value) => {
  const normalized = String(value || "")
    .replace(/[\s/_-]+/g, "")
    .toLowerCase();
  return normalized === "printed" || normalized === "readymade" || normalized === "simplereadymade";
};

const realText = (...candidates) => {
  for (const value of candidates) {
    const text = value == null ? "" : String(value).trim();
    if (text && !isLineItemKind(text)) return text;
  }
  return "";
};

const formatPhoneModel = (typeName, brandName, caseStyleName) => {
  const safeType = realText(typeName);
  const safeBrand = realText(brandName);
  const safeStyle = realText(caseStyleName);
  const base = safeBrand && safeType ? `${safeBrand} / ${safeType}` : safeType || safeBrand || "";
  if (safeStyle && base) return `${base} · ${safeStyle}`;
  return base || safeStyle || "";
};

const normalizeContextItem = (item) => {
  if (!item) return null;
  const design = item.design || item.Design;
  const type = item.type || item.Type;
  const product = item.product || item.Product;
  const variant = item.productVariant || item.ProductVariant;
  const caseStyle = item.caseStyle || item.CaseStyle;
  const typeName = realText(
    pick(type, "name", "Name"),
    pick(item, "productModel", "ProductModel"),
    pick(item, "itemType")
  );
  const brandName = realText(
    pick(item, "brandName", "BrandName"),
    pick(type?.brand || type?.Brand, "name", "Name")
  );
  const caseStyleName = realText(
    pick(item, "caseStyleName", "CaseStyleName"),
    pick(caseStyle, "name", "Name")
  );
  const productModel = formatPhoneModel(
    typeName,
    brandName,
    caseStyleName
  ) || realText(pick(item, "productModel", "ProductModel"));
  return {
    orderDetailsId: pick(item, "orderDetailsId", "OrderDetailsId"),
    designId: pick(item, "designId", "DesignId"),
    typeId: pick(item, "typeId", "TypeId"),
    productId: pick(item, "productId", "ProductId"),
    productVariantId: pick(item, "productVariantId", "ProductVariantId"),
    quantity: pick(item, "quantity", "Quantity") ?? 1,
    price: pick(item, "price", "Price"),
    customNameAr: pick(item, "customNameAr", "CustomNameAr"),
    customNameEn: pick(item, "customNameEn", "CustomNameEn"),
    designCode: pick(item, "designCode", "DesignCode") || pick(design, "code", "Code"),
    designName: pick(item, "designName", "DesignName") || pick(design, "name", "Name"),
    imageUrl:
      pick(item, "imageUrl", "ImageUrl") ||
      pick(design, "imageUrl_1", "ImageUrl_1", "imageUrl_2", "ImageUrl_2") ||
      pick(product, "imageUrl", "ImageUrl") ||
      pick(variant?.product || variant?.Product, "imageUrl", "ImageUrl"),
    productName:
      pick(item, "productName", "ProductName") ||
      pick(product, "name", "Name") ||
      pick(variant?.product || variant?.Product, "name", "Name"),
    variantCode: pick(item, "variantCode", "VariantCode") || pick(variant, "code", "Code"),
    productModel,
    itemType: typeName,
    caseStyleName,
    brandName,
    orderItemType: pick(item, "orderItemType", "OrderItemType"),
  };
};

const normalizeOrderContext = (ctx) => {
  if (!ctx) return null;
  const details = ctx.items || ctx.Items || ctx.orderDetails || ctx.OrderDetails || [];
  return {
    ...ctx,
    orderId: pick(ctx, "orderId", "OrderId"),
    name: pick(ctx, "name", "Name"),
    phoneNumber: pick(ctx, "phoneNumber", "PhoneNumber"),
    phoneNumber2: pick(ctx, "phoneNumber2", "PhoneNumber2"),
    status: pick(ctx, "status", "Status"),
    date: pick(ctx, "date", "Date"),
    totalAmount: pick(ctx, "totalAmount", "TotalAmount"),
    items: details.map(normalizeContextItem).filter(Boolean),
  };
};

const mapSearchOrderToContext = (order) => normalizeOrderContext(order);

const hydrateContextItems = async (ctx) => {
  if (!ctx?.items?.length) return ctx;
  const items = await Promise.all(
    ctx.items.map(async (item) => {
      let next = { ...item };
      try {
        if (item.designId && (!item.imageUrl || !item.designCode || !item.designName)) {
          const design = await fetchDesignById(item.designId);
          next = {
            ...next,
            imageUrl: next.imageUrl || pick(design, "imageUrl_1", "ImageUrl_1", "imageUrl_2", "ImageUrl_2"),
            designCode: next.designCode || pick(design, "code", "Code"),
            designName: next.designName || pick(design, "name", "Name"),
            productName: next.productName || pick(design, "name", "Name"),
          };
        }
      } catch {
        /* keep existing fields */
      }
      try {
        if (item.typeId && !realText(next.productModel, next.itemType)) {
          const type = await fetchTypeById(item.typeId);
          const typeName = realText(pick(type, "name", "Name"));
          const brandName = realText(
            pick(type?.brand, "name", "Name"),
            pick(type?.Brand, "name", "Name")
          );
          next = {
            ...next,
            itemType: typeName || next.itemType,
            brandName: brandName || next.brandName,
            productModel: formatPhoneModel(typeName, brandName, next.caseStyleName) || next.productModel,
          };
        }
      } catch {
        /* keep existing fields */
      }
      return next;
    })
  );
  return { ...ctx, items };
};

const phoneModelText = (item) => realText(item?.productModel, item?.itemType) || "—";
const orderIdOf = (order) => order?.orderId ?? order?.OrderId;

const itemLabel = (item) =>
  [item?.designCode, realText(item?.productModel, item?.itemType), item?.designName || item?.productName]
    .filter(Boolean)
    .join(" — ") || "قطعة";

const orderField = (ctx, camel, pascal) => ctx?.[camel] ?? ctx?.[pascal];

const CreateTicketPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const canCreate = canCreateTickets(authState.role);
  const prefillOrderRef = useRef(null);
  const selectOrderRef = useRef(null);

  const [step, setStep] = useState(1);
  const [searchMode, setSearchMode] = useState("phone");
  const [phone, setPhone] = useState("");
  const [orderIdInput, setOrderIdInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderContext, setOrderContext] = useState(null);
  const [activeTickets, setActiveTickets] = useState([]);
  const [scope, setScope] = useState("order"); // order | items
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [orderIssue, setOrderIssue] = useState(emptyIssueForm());
  const [itemIssues, setItemIssues] = useState({});
  const [priority, setPriority] = useState("Medium");
  const [assignedToUserId, setAssignedToUserId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ackDuplicates, setAckDuplicates] = useState(false);
  const idempotencyKeyRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `ticket-${Date.now()}`
  );
  const [prefilling, setPrefilling] = useState(() => {
    const raw = new URLSearchParams(window.location.search).get("orderId");
    return !!raw && /^\d+$/.test(raw);
  });

  const { data: problemTypes = [] } = useQuery({
    queryKey: ["ticketProblemTypes", "active"],
    queryFn: () => fetchTicketProblemTypes(true),
    enabled: canCreate,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["ticketAssignableUsers"],
    queryFn: fetchAssignableUsers,
    enabled: canCreate,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!authState.isValidating && !canCreate) navigate("/login");
  }, [authState, canCreate, navigate]);

  const handleSearch = async () => {
    if (searchMode === "orderId") {
      const orderId = orderIdInput.trim();
      if (!orderId) {
        toast.error("يرجى إدخال رقم الطلب");
        return;
      }
      if (!/^\d+$/.test(orderId)) {
        toast.error("رقم الطلب يجب أن يكون أرقام فقط");
        return;
      }
      setSearching(true);
      try {
        const order = await fetchOrderById(orderId);
        setOrders(order ? [order] : []);
        if (!order) toast.info("لا يوجد اوردر برقم الطلب ده");
      } catch (err) {
        setOrders([]);
        toast.error("لا يوجد اوردر برقم الطلب ده");
      } finally {
        setSearching(false);
      }
      return;
    }

    const normalized = normalizePhoneNumber(phone);
    if (!normalized || normalized.length < 8) {
      toast.error("أدخل رقم هاتف صحيح");
      return;
    }
    setSearching(true);
    try {
      const result = await searchOrdersByPhone(normalized);
      setOrders(Array.isArray(result) ? result : []);
      if (!result?.length) toast.info("لا توجد طلبات لهذا الرقم");
    } catch (err) {
      toast.error(err.message || "فشل البحث");
    } finally {
      setSearching(false);
    }
  };

  const selectOrder = async (orderId) => {
    setSelectedOrderId(orderId);
    setAckDuplicates(false);
    const searched = orders.find((o) => Number(orderIdOf(o)) === Number(orderId));
    try {
      let ctx = null;
      let active = [];
      try {
        ctx = await fetchTicketOrderContext(orderId);
      } catch (err) {
        console.error("Ticket order context failed:", err);
      }
      if (!ctx) ctx = mapSearchOrderToContext(searched);
      if (!ctx) {
        try {
          ctx = mapSearchOrderToContext(await fetchOrderById(orderId));
        } catch (err) {
          console.error("fetchOrderById fallback failed:", err);
        }
      }
      try {
        active = await fetchActiveTicketsForOrder(orderId);
      } catch {
        active = [];
      }
      if (!ctx) {
        toast.error("فشل تحميل الطلب");
        return;
      }
      ctx = await hydrateContextItems(normalizeOrderContext(ctx));
      setOrderContext(ctx);
      setActiveTickets(Array.isArray(active) ? active : []);
      setSelectedItemIds([]);
      setItemIssues({});
      setOrderIssue(emptyIssueForm());
      setScope("order");
      setStep(2);
    } catch (err) {
      toast.error(err.message || "فشل تحميل الطلب");
    } finally {
      setPrefilling(false);
    }
  };

  selectOrderRef.current = selectOrder;

  useEffect(() => {
    if (!canCreate || authState.isValidating) return;
    const raw = searchParams.get("orderId");
    if (!raw || !/^\d+$/.test(raw)) {
      setPrefilling(false);
      return;
    }
    if (prefillOrderRef.current === raw) return;
    prefillOrderRef.current = raw;
    const phoneParam = searchParams.get("phone");
    if (phoneParam) setPhone(phoneParam);
    setSearchMode("orderId");
    setOrderIdInput(raw);
    selectOrderRef.current?.(Number(raw));
  }, [canCreate, authState.isValidating, searchParams]);

  const toggleItem = (orderDetailsId) => {
    setSelectedItemIds((prev) => {
      const exists = prev.includes(orderDetailsId);
      const next = exists ? prev.filter((id) => id !== orderDetailsId) : [...prev, orderDetailsId];
      setItemIssues((issues) => {
        const copy = { ...issues };
        if (exists) delete copy[orderDetailsId];
        else if (!copy[orderDetailsId]) copy[orderDetailsId] = emptyIssueForm();
        return copy;
      });
      return next;
    });
  };

  const updateItemIssue = (id, patch) => {
    setItemIssues((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...patch },
    }));
  };

  const addAttachments = async (files, target) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    try {
      const mapped = [];
      for (const file of list) {
        const isVideo = (file.type || "").startsWith("video/");
        const isImage = (file.type || "").startsWith("image/");
        if (!isVideo && !isImage) {
          toast.error(`نوع ملف غير مدعوم: ${file.name}`);
          continue;
        }
        const max = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
        if (file.size > max) {
          toast.error(`الملف كبير جداً: ${file.name}`);
          continue;
        }
        mapped.push(await fileToTicketAttachment(file));
      }
      if (target === "order") {
        setOrderIssue((prev) => ({ ...prev, attachments: [...prev.attachments, ...mapped] }));
      } else {
        setItemIssues((prev) => ({
          ...prev,
          [target]: {
            ...(prev[target] || emptyIssueForm()),
            attachments: [...(prev[target]?.attachments || []), ...mapped],
          },
        }));
      }
    } catch (err) {
      toast.error(err.message || "فشل قراءة الملفات");
    }
  };

  const validateAndSubmit = async () => {
    if (!selectedOrderId) {
      toast.error("اختر طلباً");
      return;
    }

    let issues = [];
    if (scope === "order") {
      if (!orderIssue.problemTypeId || !orderIssue.description.trim()) {
        toast.error("أدخل نوع المشكلة والوصف للطلب");
        return;
      }
      issues = [
        {
          orderDetailsId: null,
          problemTypeId: Number(orderIssue.problemTypeId),
          description: orderIssue.description.trim(),
          attachments: orderIssue.attachments.map(({ fileName, fileType, base64Content }) => ({
            fileName,
            fileType,
            base64Content,
          })),
        },
      ];
    } else {
      if (!selectedItemIds.length) {
        toast.error("اختر عنصراً واحداً على الأقل");
        return;
      }
      for (const id of selectedItemIds) {
        const issue = itemIssues[id];
        if (!issue?.problemTypeId || !issue.description?.trim()) {
          toast.error("أكمل نوع المشكلة والوصف لكل عنصر محدد");
          return;
        }
        issues.push({
          orderDetailsId: id,
          problemTypeId: Number(issue.problemTypeId),
          description: issue.description.trim(),
          attachments: (issue.attachments || []).map(({ fileName, fileType, base64Content }) => ({
            fileName,
            fileType,
            base64Content,
          })),
        });
      }
    }

    if (activeTickets.length > 0 && !ackDuplicates) {
      toast.warning("يوجد تذاكر نشطة لهذا الطلب — أكّد المتابعة أولاً");
      return;
    }

    setSubmitting(true);
    try {
      const ticket = await createTicket({
        orderId: selectedOrderId,
        priority,
        assignedToUserId: assignedToUserId ? Number(assignedToUserId) : null,
        idempotencyKey: idempotencyKeyRef.current,
        issues,
      });
      toast.success("تم إنشاء التذكرة");
      queryClient.invalidateQueries({ queryKey: ["ticketStatusCounts"] });
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      toast.error(err.message || "فشل إنشاء التذكرة");
    } finally {
      setSubmitting(false);
    }
  };

  if (!canCreate) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/tickets")} className="p-2 rounded-xl bg-white shadow">
            <ChevronRight size={22} />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">إنشاء تذكرة جديدة</h1>
            <p className="text-gray-500">الخطوة {step} من 3</p>
          </div>
        </div>

        <div className="flex gap-2 mb-6">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className={`flex-1 h-2 rounded-full ${step >= n ? "bg-indigo-600" : "bg-gray-200"}`}
            />
          ))}
        </div>

        {prefilling && step === 1 && (
          <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-8 text-center text-gray-600">
            جاري تحميل الطلب...
          </div>
        )}

        {step === 1 && !prefilling && (
          <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-5 space-y-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Search size={20} /> البحث عن العميل والطلب
            </h2>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSearchMode("phone")}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold transition-all ${
                  searchMode === "phone"
                    ? "bg-indigo-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <Phone size={18} />
                رقم الهاتف
              </button>
              <button
                type="button"
                onClick={() => setSearchMode("orderId")}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-bold transition-all ${
                  searchMode === "orderId"
                    ? "bg-indigo-600 text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <Hash size={18} />
                رقم الطلب
              </button>
            </div>
            <div className="flex flex-col md:flex-row gap-3">
              {searchMode === "phone" ? (
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="أدخل رقم الهاتف للبحث..."
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl"
                />
              ) : (
                <input
                  type="text"
                  inputMode="numeric"
                  value={orderIdInput}
                  onChange={(e) => setOrderIdInput(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  placeholder="أدخل رقم الطلب للبحث..."
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl"
                />
              )}
              <button
                onClick={handleSearch}
                disabled={searching}
                className="px-5 py-3 rounded-xl bg-indigo-600 text-white font-semibold disabled:opacity-50"
              >
                {searching ? "جاري البحث..." : "بحث"}
              </button>
            </div>

            <div className="space-y-3">
              {orders.map((o) => {
                const id = orderIdOf(o);
                return (
                  <button
                    key={id}
                    onClick={() => selectOrder(id)}
                    className="w-full text-right bg-gray-50 hover:bg-indigo-50 border border-gray-200 rounded-xl p-4 transition"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-bold text-indigo-700">طلب #{id}</span>
                      <span className="text-sm px-2 py-1 rounded-full bg-white border">{o.status || o.Status}</span>
                    </div>
                    <div className="mt-2 text-sm text-gray-700">
                      {o.name || o.Name} — {o.phoneNumber || o.PhoneNumber}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {formatDateTime(o.date || o.Date)} — {(o.orderDetails || o.OrderDetails)?.length || 0} أسطر منتجات
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && orderContext && (
          <div className="space-y-4">
            <OrderContextBanner context={orderContext} />
            <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-5">
              <h2 className="text-xl font-bold mb-3">نطاق المشكلة</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  onClick={() => setScope("order")}
                  className={`p-4 rounded-xl border-2 text-right ${
                    scope === "order" ? "border-indigo-600 bg-indigo-50" : "border-gray-200"
                  }`}
                >
                  <Layers className="mb-2 text-indigo-600" />
                  <div className="font-bold">الطلب بالكامل</div>
                  <div className="text-sm text-gray-500">مشكلة عامة على مستوى الطلب</div>
                </button>
                <button
                  onClick={() => setScope("items")}
                  className={`p-4 rounded-xl border-2 text-right ${
                    scope === "items" ? "border-indigo-600 bg-indigo-50" : "border-gray-200"
                  }`}
                >
                  <Package className="mb-2 text-indigo-600" />
                  <div className="font-bold">عناصر محددة</div>
                  <div className="text-sm text-gray-500">اختر منتجاً أو أكثر من الطلب</div>
                </button>
              </div>

              {scope === "items" && (
                <div className="mt-4 space-y-3">
                  {(orderContext.items || []).map((item) => {
                    const selected = selectedItemIds.includes(item.orderDetailsId);
                    const imageSrc = resolveMediaUrl(item.imageUrl);
                    return (
                      <label
                        key={item.orderDetailsId}
                        className={`flex gap-3 p-3 rounded-xl border cursor-pointer ${
                          selected ? "border-indigo-500 bg-indigo-50/60" : "border-gray-200"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleItem(item.orderDetailsId)}
                          className="mt-2"
                        />
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt=""
                            className="w-16 h-16 rounded-lg object-cover bg-gray-100"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                            <Package size={22} />
                          </div>
                        )}
                        <div className="flex-1 text-sm">
                          <div className="font-semibold">
                            {item.designName || item.productName || item.designCode || "قطعة"}
                          </div>
                          <div className="text-gray-700 font-medium">
                            كود الديزاين: {item.designCode || "—"}
                          </div>
                          <div className="text-gray-600">
                            نوع الموبايل: {phoneModelText(item)}
                          </div>
                          <div className="text-gray-500">الكمية: {item.quantity ?? 1}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-between mt-5">
                <button onClick={() => setStep(1)} className="px-4 py-2 rounded-xl border">
                  رجوع
                </button>
                <button
                  onClick={() => {
                    if (scope === "items" && !selectedItemIds.length) {
                      toast.error("اختر عنصراً واحداً على الأقل");
                      return;
                    }
                    setStep(3);
                  }}
                  className="px-5 py-2 rounded-xl bg-indigo-600 text-white"
                >
                  التالي
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && orderContext && (
          <div className="space-y-4">
            <OrderContextBanner context={orderContext} />
            {activeTickets.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                <div className="flex items-start gap-2 text-amber-800 font-semibold">
                  <AlertTriangle size={18} className="mt-0.5" />
                  يوجد {activeTickets.length} تذكرة نشطة لهذا الطلب
                </div>
                <div className="mt-3 space-y-2">
                  {activeTickets.map((t) => (
                    <div key={t.id} className="bg-white rounded-xl p-3 text-sm flex flex-wrap gap-2 items-center">
                      <span className="font-bold">#{t.id}</span>
                      <TicketStatusBadge status={t.status} />
                      <TicketPriorityBadge priority={t.priority} />
                      <span className="text-gray-500">{formatDateTime(t.createdAtUtc)}</span>
                      <button
                        type="button"
                        className="text-indigo-600 underline"
                        onClick={() => navigate(`/tickets/${t.id}`)}
                      >
                        عرض
                      </button>
                    </div>
                  ))}
                </div>
                <label className="flex items-center gap-2 mt-3 text-sm">
                  <input
                    type="checkbox"
                    checked={ackDuplicates}
                    onChange={(e) => setAckDuplicates(e.target.checked)}
                  />
                  أفهم ذلك وأرغب بإنشاء تذكرة جديدة لمشكلة مختلفة
                </label>
              </div>
            )}

            <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-5 space-y-4">
              <h2 className="text-xl font-bold">تفاصيل المشكلة</h2>

              {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600">الأولوية</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                  >
                    {TICKET_PRIORITIES.map((p) => (
                      <option key={p} value={p}>
                        {PRIORITY_LABELS_AR[p]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-600">تعيين إلى (اختياري)</label>
                  <select
                    value={assignedToUserId}
                    onChange={(e) => setAssignedToUserId(e.target.value)}
                    className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                  >
                    <option value="">بدون تعيين</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.email} ({u.role})
                      </option>
                    ))}
                  </select>
                </div>
              </div> */}

              {scope === "order" ? (
                <IssueEditor
                  title="مشكلة على مستوى الطلب"
                  value={orderIssue}
                  onChange={setOrderIssue}
                  problemTypes={problemTypes}
                  onAddFiles={(files) => addAttachments(files, "order")}
                />
              ) : (
                selectedItemIds.map((id) => {
                  const item = (orderContext.items || []).find((i) => i.orderDetailsId === id);
                  return (
                    <IssueEditor
                      key={id}
                      title={itemLabel(item)}
                      previewUrl={resolveMediaUrl(item?.imageUrl)}
                      meta={`كود الديزاين: ${item?.designCode || "—"} | نوع الموبايل: ${phoneModelText(item)} | الكمية: ${item?.quantity || 1}`}
                      value={itemIssues[id] || emptyIssueForm()}
                      onChange={(v) => updateItemIssue(id, v)}
                      problemTypes={problemTypes}
                      onAddFiles={(files) => addAttachments(files, id)}
                    />
                  );
                })
              )}

              <div className="flex justify-between pt-2">
                <button onClick={() => setStep(2)} className="px-4 py-2 rounded-xl border">
                  رجوع
                </button>
                <button
                  onClick={validateAndSubmit}
                  disabled={submitting}
                  className="px-5 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold flex items-center gap-2 disabled:opacity-50"
                >
                  <CheckCircle2 size={18} />
                  {submitting ? "جاري الإنشاء..." : "إنشاء التذكرة"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

function OrderContextBanner({ context }) {
  const orderId = orderField(context, "orderId", "OrderId");
  const phone = orderField(context, "phoneNumber", "PhoneNumber");
  const name = orderField(context, "name", "Name");
  return (
    <div className="bg-white/90 rounded-2xl shadow border border-indigo-100 p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
      <div>
        <div className="text-xs text-gray-500">رقم الطلب</div>
        <div className="text-xl font-bold text-indigo-700">#{orderId}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">رقم الموبايل</div>
        <div className="font-semibold text-gray-800">{phone || "—"}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">العميل</div>
        <div className="font-semibold text-gray-800">{name || "—"}</div>
      </div>
    </div>
  );
}

function IssueEditor({ title, previewUrl, meta, value, onChange, problemTypes, onAddFiles }) {
  return (
    <div className="border border-gray-200 rounded-2xl p-4 bg-gray-50/70 space-y-3">
      <div className="flex gap-3 items-start">
        {previewUrl && (
          <img src={previewUrl} alt="" className="w-16 h-16 rounded-lg object-cover bg-white" />
        )}
        <div>
          <div className="font-bold text-gray-800">{title}</div>
          {meta && <div className="text-xs text-gray-500 mt-1">{meta}</div>}
        </div>
      </div>
      <select
        value={value.problemTypeId}
        onChange={(e) => onChange({ ...value, problemTypeId: e.target.value })}
        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl bg-white"
      >
        <option value="">اختر نوع المشكلة</option>
        {problemTypes.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <textarea
        rows={3}
        value={value.description}
        onChange={(e) => onChange({ ...value, description: e.target.value })}
        placeholder="وصف المشكلة..."
        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl bg-white"
      />
      <div>
        <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 bg-white cursor-pointer text-sm">
          <Upload size={16} />
          رفع صور / فيديو
          <input
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={(e) => {
              onAddFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </label>
        <div className="flex flex-wrap gap-2 mt-2">
          {(value.attachments || []).map((att, idx) => (
            <AttachmentPreview
              key={`${att.fileName}-${idx}`}
              attachment={att}
              onRemove={() =>
                onChange({
                  ...value,
                  attachments: value.attachments.filter((_, i) => i !== idx),
                })
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default CreateTicketPage;
